import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { type ReactNode, useState } from 'react';
import type { CreativeDetailTab } from '../../../lib/creative-detail-search';
import type { DetailCreative } from '../../creatives/data/api';
import { CreativeDetailDrawer } from '../../creatives/detail/CreativeDetailDrawer';
import { ReportSection } from '../ReportCard';
import { CreativeDna } from './CreativeDna';
import { type PerformanceReport, type ReportRequest, usePerformanceReport } from './data/api';
import { FatigueSection } from './FatigueSection';
import { KpiRow } from './KpiRow';
import { NextPeriodActions } from './NextPeriodActions';
import { OpportunityMatrix } from './OpportunityMatrix';
import { Performers } from './Performers';
import { ReportHeader } from './ReportHeader';
import { TrendChart } from './TrendChart';

interface OpenedCreative {
  creativeId: string;
  tab: CreativeDetailTab;
}

/** Every creative the report lists, in page order, so the drawer can step between them. The
 * report carries no delivery data, so days live, network and type are left empty. */
function listedCreatives(report: PerformanceReport): DetailCreative[] {
  const rows = [
    ...Object.values(report.matrix.quadrants).flat(),
    ...report.performers.top,
    ...report.performers.low,
  ];
  const byCreative = new Map<string, (typeof rows)[number]>();
  for (const row of rows) {
    if (row.creativeId !== null && !byCreative.has(row.creativeId)) {
      byCreative.set(row.creativeId, row);
    }
  }
  return [...byCreative].map(([creativeId, row]) => ({
    creativeId,
    thumbnailUrl: row.thumbnailUrl ?? '',
    previewUrl: row.previewUrl ?? '',
    duration: null,
    format: null,
    network: null,
    creativeType: null,
    spendRatio: null,
    activeDays: null,
    firstSeen: null,
    lastSeen: null,
    metrics: {},
    uplift: {},
  }));
}

export function PerformanceReportView({ request }: { request: ReportRequest }): ReactNode {
  const { data: report, isPending, error } = usePerformanceReport(request);
  const [opened, setOpened] = useState<OpenedCreative | null>(null);
  const handleOpenCreative = (creativeId: string): void =>
    setOpened({ creativeId, tab: 'overview' });

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (isPending || report === undefined) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 10, pb: 12 }}>
      <ReportHeader report={report} />
      <KpiRow report={report} />

      <ReportSection
        title="Period in motion"
        description={`Spend and ${report.kpi.label} across the period.`}
      >
        <TrendChart report={report} />
      </ReportSection>

      <ReportSection
        title="Opportunity matrix"
        description={`Active creatives split by spend and ${report.kpi.label}. Selecting a quadrant lists its creatives and the elements they share.`}
      >
        <OpportunityMatrix report={report} onOpenCreative={handleOpenCreative} />
      </ReportSection>

      <ReportSection
        title="Performers"
        description="The widest gaps in both directions on meaningful spend."
      >
        <Performers report={report} onOpenCreative={handleOpenCreative} />
      </ReportSection>

      <ReportSection
        title="Creative DNA"
        description="The elements the winning creatives share, and how far they run ahead of the competitive set."
      >
        <CreativeDna report={report} />
      </ReportSection>

      <ReportSection
        title="Fatigued creatives"
        description="What is fatigued now, how much spend sits on it, and which top spenders are on track to breach."
      >
        <FatigueSection report={report} onOpenCreative={handleOpenCreative} />
      </ReportSection>

      <ReportSection
        title="Next period"
        description="Four moves, straight off this period's tag and creative performance."
      >
        <NextPeriodActions report={report} onOpenCreative={handleOpenCreative} />
      </ReportSection>

      {opened !== null && (
        <CreativeDetailDrawer
          creatives={listedCreatives(report)}
          creativeId={opened.creativeId}
          tab={opened.tab}
          scope={{ dateRange: report.periods.current.dateRange, accountIds: request.accountIds }}
          kpis={[report.kpi.name]}
          leadingKpiLabel={report.kpi.label}
          onOpen={handleOpenCreative}
          onTabChange={(tab) => setOpened({ ...opened, tab })}
          onClose={() => setOpened(null)}
        />
      )}
    </Box>
  );
}
